import { useState } from 'react';
import moment from 'moment';
import { Alert, Box, Button, Stack, TextField } from '../../ui/core';
import api from '../../lib/api';
import { DESCRIPTION_MAX, validateManualPolicyChange } from './sdpFormatters';

const LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';
const EMPTY = { effectiveAt: '', description: '', affects: '' };

// effectiveAt is entered in the admin's local time (datetime-local) and sent
// as an ISO instant.
const toIso = (local) => (local ? moment(local, LOCAL_FORMAT).toISOString() : '');

const ManualPolicyChangeForm = ({ cities = [], onCreated }) => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const set = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setCreated(null);
    setServerError(null);
    const body = {
      effectiveAt: toIso(values.effectiveAt),
      description: values.description.trim(),
      affects: values.affects,
    };
    const nextErrors = validateManualPolicyChange(body);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitting(true);
    try {
      const entry = await api.supplyDemandPace.createPolicyChange(body);
      setCreated(entry);
      setValues(EMPTY);
      onCreated && (await onCreated(entry));
    } catch (err) {
      setServerError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} noValidate data-testid="manual-policy-change-form">
      <Stack spacing="12px">
        <Alert severity="warning">
          The nightly job treats a manual row as a hard reset: the refit window restarts for whatever it affects (a city,
          or everything when global). Use it for real policy changes (fares, products, ops rules), not notes.
        </Alert>
        <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <TextField
            name="effectiveAt"
            label="Effective at (local time)"
            type="datetime-local"
            size="small"
            value={values.effectiveAt}
            onChange={set('effectiveAt')}
            error={!!errors.effectiveAt}
            helperText={errors.effectiveAt || 'Not in the future'}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: moment().format(LOCAL_FORMAT) }}
          />
          <TextField
            select
            name="affects"
            label="Affects"
            size="small"
            value={values.affects}
            onChange={set('affects')}
            error={!!errors.affects}
            helperText={errors.affects || ' '}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          >
            <option value="">Select…</option>
            <option value="global">global</option>
            {cities.map((c) => (
              <option key={c.cityId} value={c.cityId}>
                {c.cityName || c.cityId}
              </option>
            ))}
          </TextField>
        </Box>
        <TextField
          name="description"
          label="Description"
          value={values.description}
          onChange={set('description')}
          error={!!errors.description}
          helperText={errors.description || `${values.description.trim().length}/${DESCRIPTION_MAX}`}
          multiline
          minRows={2}
          fullWidth
        />
        {serverError && <Alert severity="error">{serverError}</Alert>}
        {created && <Alert severity="success">Manual policy change added.</Alert>}
        <Box>
          <Button type="submit" variant="contained" disabled={submitting} disableElevation>
            Add manual policy change
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default ManualPolicyChangeForm;
